import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle2, Send } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { enquiriesAPI } from '../../api';
import { loadSavedForm, saveForm, clearSavedForm } from './formUtils';

/**
 * Single-page enquiry form for services that don't go through checkout
 * (Cooperative, Business Plan, Company Profile, COIDA).
 */
const EnquiryForm = ({ service }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const storageKey = `enquiry:${service.id}`;
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState(
    () =>
      loadSavedForm(storageKey) || {
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        companyName: '',
        message: '',
      },
  );

  useEffect(() => {
    saveForm(storageKey, form);
  }, [storageKey, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contactName || !form.contactEmail) {
      toast({
        title: 'Required Fields Missing',
        description: 'Name and email are required.',
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      const { data } = await enquiriesAPI.create({
        serviceId: service.id,
        serviceName: service.name,
        routeTo: service.enquiryEmail || 'hello@consultiumgroup.co.za',
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        message: form.message,
        extra: { companyName: form.companyName },
      });
      clearSavedForm(storageKey);
      setSubmitted(data.enquiryId);
      toast({
        title: 'Enquiry sent',
        description: `Your reference is ${data.enquiryId}. We'll be in touch within 1 business day.`,
      });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh]">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
              <h2 className="text-2xl font-bold text-gray-900">Thanks — we&apos;ve got your enquiry</h2>
              <p className="text-gray-600">
                Reference <strong>{submitted}</strong>. A consultant will email{' '}
                <strong>{form.contactEmail}</strong> with next steps within 1 business day.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="enquiry-back-home"
              >
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="enquiry-form">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{service.name} — Request a Quote</h1>
      <p className="text-gray-600 mb-6">
        Tell us a bit about your needs and we&apos;ll come back with pricing &amp; turnaround
        within 1 business day.
      </p>
      <Card>
        <CardHeader>
          <CardTitle>Your Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={form.contactName}
                  onChange={(e) => set('contactName', e.target.value)}
                  data-testid="enquiry-name"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                  data-testid="enquiry-email"
                  required
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input
                  value={form.contactPhone}
                  onChange={(e) => set('contactPhone', e.target.value)}
                  data-testid="enquiry-phone"
                />
              </div>
              <div>
                <Label>Company Name</Label>
                <Input
                  value={form.companyName}
                  onChange={(e) => set('companyName', e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label>Message / Requirements</Label>
              <textarea
                className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[120px]"
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                placeholder="Briefly describe what you need..."
                data-testid="enquiry-message"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
              data-testid="enquiry-submit"
            >
              <Send className="h-4 w-4 mr-2" />
              {busy ? 'Sending…' : 'Send Enquiry'}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Sent to {service.enquiryEmail || 'hello@consultiumgroup.co.za'}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnquiryForm;
